'use client'
import React, { FormEvent, useState, ReactElement } from 'react'
import Card from '@/components/controls/Card'
import Input from '@/components/controls/Input'
import Button from '@/components/controls/Button'
import Message, { MessageProps } from '@/components/controls/Message'
import logger from '@/lib/utils/logger'
import { ResponseError } from '@/lib/utils/errors'

const ResetPasswordForm = ({showLogin, initialMessage}: {showLogin: () => void, initialMessage?: MessageProps}): ReactElement => {
  const [email, setEmail] = useState<string>('')
  const [customerId, setCustomerId] = useState<string>('')
  const [hideCustomerId, setHideCustomerId] = useState<boolean>(true)
  const [message, setMessage] = useState<MessageProps | null>(initialMessage || null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [sent, setSent] = useState<boolean>(false)

  const clearMessage = () => {
    setMessage(null)
  }

  const handleReset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setMessage(null)
    setIsSubmitting(true)

    try {
      const response = await fetch('/api/auth/password/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, customerId })
      })

      if (!response.ok) {
        const errorData = await response.json()
        if (errorData.message === 'Account # required') {
          setHideCustomerId(false)
          setMessage({
            type: 'info', 
            children: 'Please enter your Account # to proceed. It can be found on your last invoice.'
          })
          setIsSubmitting(false)
          return
        }
        throw new ResponseError('Failed to send reset email', response)
      }

      const result = await response.json()
      setMessage({ 
        type: 'success', 
        children: result.message || 'If the information you provided matches our records, a password reset email will be sent.'
      })
      setSent(true)
      setCustomerId('')
      setHideCustomerId(true)
    } catch (error) {
      logger.error('Error resetting password:', error)
      setMessage({ 
        type: 'error', 
        children: 'Failed to send reset email', 
        timeout: 5000, 
        afterTimeout: clearMessage 
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="w-full max-w-md">
      <form onSubmit={handleReset}>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" />
        <Input type={hideCustomerId ? "hidden" : "text"} value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="Enter your Account #" />
        { message && 
          <Message {...message} />
        }
        { !sent &&
          <Button className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-400 hover:to-blue-400 active:from-green-800 active:to-blue-800 text-white font-bold" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send Reset Email'}
          </Button>
        }
      </form>
      <div className="text-center mt-4">
        <a className="inline-block align-baseline font-bold text-sm text-blue-500 hover:text-blue-800 cursor-pointer" onClick={showLogin}>
          Back to Login
        </a>
      </div>
    </Card>
  )
}

export default ResetPasswordForm